import preventDefault from '@mailobj-browser/front/js/events/hooks/preventDefault.js'
import before from '@mailobj-browser/front/js/tree/before.js'
import replaceWith from '@mailobj-browser/front/js/tree/replaceWith.js'
import element from '@mailobj-browser/front/js/tree/element.js'
import listener from '@mailobj-browser/front/js/events/listeners/listener.js'
import one from '@mailobj-browser/front/js/selectors/one.js'
import drop from '@mailobj-browser/front/js/events/types/drop.js'
import dragLeave from '@mailobj-browser/front/js/events/types/dragLeave.js'
import dragOver from '@mailobj-browser/front/js/events/types/dragOver.js'
import object from '@mailobj-browser/front/js/utils/object.js'
import { draggable } from './drag.js'
import all from '@mailobj-browser/front/js/selectors/all.js'
import { modifiers } from '../styles.js'
import { bottomRight, fromEvent, move } from '#utilities/js/fixed/fixed.js'
import array from '@mailobj-browser/front/js/utils/array.js'
import manager from '@mailobj-browser/front/js/contracts/manager.js'
import append from '@mailobj-browser/front/js/tree/append.js'

const dropZones = new WeakMap()

const onDragOver = object(listener, {
  type: dragOver,
  task: async (
    dropZone,
    event
  ) => {
    const { dragged, type } = draggable()
    const handlers = dropZones.get(dropZone) ?? []
    const effect = dropZone.getAttribute('data-drop-effect')

    if (effect.startsWith('position:')) {
      dropZone.classList.add(modifiers.drop.hover)
      preventDefault(event)

      return
    }

    for (const [key] of handlers) {
      if (key === type) {
        if (!dropZone.contains(dragged)) {
          dropZone.classList.add(modifiers.drop.hover)
          preventDefault(event)

          break
        }

        const destination = movable(dragged, dropZone, event)

        if (destination) {
          destination.classList.add(modifiers.drop.hover)
          preventDefault(event)
        }
      }
    }
  }
})

const onDragLeave = object(listener, {
  type: dragLeave,
  hooks: array([
    preventDefault
  ]),
  task: async (
    dropZone
  ) => {
    const hover = one(`.${modifiers.drop.hover}`, dropZone) ?? dropZone

    hover.classList.remove(modifiers.drop.hover)
  }
})

export const onDrop = object(listener, {
  type: drop,
  task: async (
    dropZone,
    event
  ) => {
    const { clientX, clientY, dragged, type } = draggable()

    if (!dragged) {
      preventDefault(event)

      return
    }

    const element = await getElement(dragged, dropZone)
    const handlers = dropZones.get(dropZone) ?? []
    const [effect, value] = dropZone.getAttribute('data-drop-effect')?.split(':') ?? []

    if (effect === 'position') {
      dropZone.classList.remove(modifiers.drop.hover)
      preventDefault(event)
      effects[effect]?.(element, event, value, { clientX, clientY })

      return
    }

    for (const [key, handler] of handlers) {
      if (key === type) {
        if (!dropZone.contains(element)) {
          preventDefault(event)
          await handler(dropZone, element, event)

          break
        }

        const destination = movable(element, dropZone, event)

        if (destination) {
          effects[effect]?.(element, destination, event)
          preventDefault(event)
        }
      }
    }

    await onDragLeave.task(dropZone)
  }
})

const getElement = async (
  dragged,
  dropZone
) => {
  const { dataset, parentNode } = dragged
  const { dropClone } = dataset

  if (!dropClone) {
    return dragged
  }

  const clone = dragged.cloneNode(true)

  await manager.fragment(clone)
  append(dropZone, clone)
  clone.removeAttribute('data-drop-clone')

  return clone
}

const effects = object(null, {
  order (dragged, destination) {
    before(destination, dragged)
  },
  position (dragged, event, position, origin) {
    move(dragged, positions[position](dragged, event, origin))
  },
  swap (dragged, destination) {
    const template = element(dragged, 'template')

    replaceWith(destination, template)
    replaceWith(dragged, destination)
    replaceWith(template, dragged)
  }
})

const positions = object(null, {
  absolute: (dragged, event, origin) => {
    const { clientX: eventX, clientY: eventY } = event
    const { clientX: originX, clientY: originY } = origin
    const { offsetLeft: draggedLeft, offsetTop: draggedTop } = dragged

    return object(null, {
      clientX: eventX - originX + draggedLeft,
      clientY: eventY - originY + draggedTop
    })
  },
  fixed: (dragged, event, origin) => {
    return fromEvent(dragged, event, bottomRight, origin)
  }
})

const movable = (
  dragged,
  dropZone,
  event
) => {
  try {
    const effect = dropZone.getAttribute('data-drop-effect')
    const { target } = event
    const destination = target.closest('[draggable="true"]')

    return destination !== dragged &&
      Object.hasOwn(effects, effect) &&
      all(`[draggable="true"]`, dropZone).length > 1 &&
      destination
  } catch {}
}

export default (
  dropZone
) => {
  onDragLeave.listen(dropZone)
  onDragOver.listen(dropZone)
  onDrop.listen(dropZone)
}

export const dropZone = (
  dropZone,
  { ...handlers }
) => {
  dropZones.set(dropZone, [
    ...dropZones.get(dropZone) ?? [],
    ...Object.entries(handlers)
  ])
}
